import { useState } from "react";
import { Calculator, Gift } from "lucide-react";
import Reveal from "@/components/Reveal";
import { REBATE_PER_LOT, PRIZES, formatUSD } from "@/data/site";

const PRESETS = [10, 50, 100, 250, 500, 1000];

export default function RebateSection() {
  const [lots, setLots] = useState(50);
  const total = Math.max(0, Math.min(lots, 100000) * REBATE_PER_LOT);
  const unlocked = PRIZES.filter((p) => p.lots && lots >= p.lots);
  const fillPct = (Math.min(lots, 2000) / 2000) * 100;

  return (
    <section id="rebate" className="relative overflow-hidden py-20 sm:py-28">
      <div className="pointer-events-none absolute right-0 top-1/4 h-[420px] w-[420px] rounded-full bg-[#00F2B6]/10 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal testId="rebate-intro">
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2B6] sm:text-sm">
              Rebate untuk Kamu
            </p>
            <h2 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-5xl">
              Setiap Lot, <span className="text-gradient-gold">US$15</span> Buat Kamu
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-400 sm:text-base">
              Rebate dihitung dengan rumus sederhana: lot yang memenuhi syarat
              dikalikan US$15. Semakin konsisten kamu trading, semakin banyak
              rebate yang terkumpul.
            </p>

            <div className="mt-7 space-y-3">
              {[
                { lots: "1 lot", usd: "US$15" },
                { lots: "10 lot", usd: "US$150" },
                { lots: "100 lot", usd: "US$1.500" },
              ].map((ex) => (
                <div
                  key={ex.lots}
                  data-testid={`rebate-example-${ex.lots.replace(/[^a-z0-9]/gi, "-")}`}
                  className="glass flex items-center justify-between rounded-2xl px-5 py-3.5"
                >
                  <span className="font-mono text-sm font-bold text-zinc-300">
                    {ex.lots}
                  </span>
                  <span className="font-mono text-sm font-bold text-[#00F2B6] sm:text-base">
                    = {ex.usd}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-6 max-w-lg text-xs leading-relaxed text-zinc-500 sm:text-sm">
              Ketentuan lengkap dan proses pencairan rebate dikonfirmasikan
              langsung melalui admin.
            </p>
          </Reveal>

          <Reveal delay={0.15} testId="rebate-calculator">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0D111A] p-6 sm:p-8">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#F0B429]/20 blur-3xl" />
              <div className="flex items-center gap-3">
                <span className="glass flex h-11 w-11 items-center justify-center rounded-2xl">
                  <Calculator className="h-5 w-5 text-[#00F2B6]" />
                </span>
                <h3 className="text-lg font-bold text-white sm:text-xl">
                  Kalkulator Rebate
                </h3>
              </div>

              <label
                htmlFor="rebate-lots"
                className="mt-7 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500"
              >
                Lot yang memenuhi syarat
              </label>
              <input
                id="rebate-lots"
                type="number"
                min="0"
                max="100000"
                value={lots}
                onChange={(e) =>
                  setLots(e.target.value === "" ? 0 : Number(e.target.value))
                }
                data-testid="calculator-lot-input-number"
                className="mt-2 h-14 w-full rounded-2xl border border-white/10 bg-[#07090E] px-5 font-mono text-2xl font-bold text-white outline-none transition-colors focus:border-[#00F2B6]"
              />

              <input
                type="range"
                min="1"
                max="2000"
                value={Math.min(lots, 2000) || 1}
                onChange={(e) => setLots(Number(e.target.value))}
                data-testid="calculator-lot-input-slider"
                aria-label="Atur jumlah lot"
                className="rebate-slider mt-6 w-full"
                style={{ "--fill": `${fillPct}%` }}
              />

              <div className="mt-5 flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    data-testid={`calculator-preset-${p}`}
                    onClick={() => setLots(p)}
                    className={`rounded-full px-4 py-1.5 font-mono text-xs font-bold transition-all ${
                      lots === p
                        ? "bg-[#F0B429] text-white"
                        : "glass text-zinc-400 hover:text-white"
                    }`}
                  >
                    {p} lot
                  </button>
                ))}
              </div>

              <div className="mt-7 rounded-2xl border border-[#00F2B6]/25 bg-[#00F2B6]/5 p-5 text-center">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Total Rebate
                </p>
                <p
                  data-testid="calculator-rebate-result-usd"
                  className="mt-1 font-display text-4xl font-black text-[#00F2B6] sm:text-5xl"
                >
                  {formatUSD(total)}
                </p>
                <p className="mt-2 font-mono text-xs text-zinc-500">
                  {Math.max(0, Math.min(lots, 100000)).toLocaleString("id-ID")} lot × US$15
                </p>
              </div>

              <div
                data-testid="calculator-unlocked-prize-tier"
                className="mt-5 flex items-start gap-3 rounded-2xl border border-white/10 bg-[#07090E] p-4"
              >
                <Gift className="mt-0.5 h-4 w-4 shrink-0 text-[#F0B429]" />
                <p className="text-xs leading-relaxed text-zinc-400 sm:text-sm">
                  {unlocked.length > 0 ? (
                    <>
                      Sudah melewati target:{" "}
                      <span className="font-bold text-white">
                        {unlocked.map((p) => p.name).join(", ")}
                      </span>
                    </>
                  ) : (
                    "Belum ada target hadiah yang terlewati — lanjut kumpulkan lotnya!"
                  )}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
